import { Formik, FormikConfig } from 'formik';
import { makeValidator } from 'mandle';
import { parse } from 'qs';
import * as React from 'react';
import { Redirect, RouteComponentProps } from 'react-router-dom';

import { Input } from '../../components/Input';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { useTitle } from '../../useTitle';
import { formatErrors } from '../../utils/formatErrors';
import { makeInputPropsMaker } from '../../utils/inputPropsMaker';

import { required2, minLength } from '../../validators';
import { useUpdatePasswordMutation } from '../../generated/graphql';
import { useAlert } from '../../contexts/AlertContext';
import { SubmitButton } from '../../components/SubmitButton';
import { ButtonLink } from '../../components/Button';
import { paths } from '../../paths';
import { LoginLayout } from '../../layouts/LoginLayout';

interface Props extends RouteComponentProps {}

interface Fields {
  password: string;
  token: string;
}

const validate = makeValidator<Fields>({
  password: [required2('Enter a password'), minLength(6)],
});

const TokenError: React.FC = () => {
  return (
    <LoginLayout>
      <div className="space-y-6">
        <h1 className="font-semibold text-center">
          The link you've used has expired or is invalid 😭
        </h1>

        <ButtonLink to={paths.password.forgot()} icon="chevron-right">
          Request another reset email
        </ButtonLink>
      </div>
    </LoginLayout>
  );
};

export const ResetPassword = ({ location }: Props) => {
  const token: string = parse(location.search, { ignoreQueryPrefix: true })[
    'token'
  ];
  if (!token) {
    return <Redirect to={paths.signIn()} />;
  }

  return <ResetPasswordForm token={token} />;
};

interface ResetPasswordFormProps {
  token: string;
}

function ResetPasswordForm({ token }: ResetPasswordFormProps) {
  const analytics = useAnalytics();
  useTitle('Reset Password');
  const alert = useAlert();

  const [updatePasswordResponse, updatePassword] = useUpdatePasswordMutation();

  if (updatePasswordResponse.error?.graphQLErrors) {
    return <TokenError />;
  }

  const submitForm: FormikConfig<Fields>['onSubmit'] = (variables, actions) => {
    analytics.track('Form Submitted', {
      form: 'reset_password',
    });
    updatePassword(variables)
      .then((r) => {
        if (r && r.data && r.data.updatePassword) {
          if (r.data.updatePassword.validation) {
            actions.setErrors(formatErrors(r.data.updatePassword.validation));
            return;
          }

          alert('Password updated. Please log in.');
        }
      })
      .catch((error) => {
        analytics.track('Data Submit Failed', {
          form: 'reset_password',
          error,
        });
        alert('something went wrong 😭');
      });
  };

  if (updatePasswordResponse.data?.updatePassword) {
    return <Redirect to="/login" />;
  }

  return (
    <LoginLayout>
      <h1 className="font-semibold mb-4 text-center">Reset your password</h1>
      <Formik
        initialValues={{ password: '', token }}
        onSubmit={submitForm}
        validate={validate}
      >
        {(formProps) => {
          const getInputProps = makeInputPropsMaker(formProps);
          return (
            <form
              noValidate={true}
              onSubmit={formProps.handleSubmit}
              className="space-y-6"
            >
              <Input
                label="New password"
                type="password"
                {...getInputProps('password')}
              />

              <SubmitButton loading={updatePasswordResponse.fetching}>
                Reset password
              </SubmitButton>
            </form>
          );
        }}
      </Formik>
    </LoginLayout>
  );
}
